import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import { Box, BoxProps } from '@mui/material';

import {
  HiddenElementsContext,
  OverflowCounterContext,
  OverflowCounterState,
} from './context';

type Props = PropsWithChildren<BoxProps> & {
  /**
   * Threshold for the IntersectionObserver.
   * The default is 0 (meaning as soon as even one pixel is visible, the element is considered visible).
   * The number should be between 0 and 1 (where 1 means the element is considered visible when all of its pixels are visible).
   */
  visibilityThreshold?: number;
};

export const OverflowCounter = ({ visibilityThreshold, ...props }: Props) => {
  const ref = useRef<HTMLDivElement>(undefined);
  const [hiddenElements, setHiddenElements] = useState<string[]>([]);
  const observerRef = useRef<IntersectionObserver>(undefined);
  const elementsToObserve = useRef<HTMLElement[]>([]);
  const isUnmounted = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        setHiddenElements((hiddenElements) => {
          const newEntries = new Set(hiddenElements);

          for (const entry of entries) {
            const overflowKey = entry.target.getAttribute('data-overflowkey');
            if (overflowKey === null) {
              throw new Error('No overflowkey attribute value');
            }
            if (entry.isIntersecting) {
              newEntries.delete(overflowKey);
            } else {
              newEntries.add(overflowKey);
            }
          }
          return Array.from(newEntries);
        });
      },
      { root: ref.current, threshold: visibilityThreshold }
    );
    observerRef.current = observer;
    // Need to run through elementsToObserve, because it was not defined when observeElement was called
    for (const el of elementsToObserve.current) {
      observer.observe(el);
    }
    return () => {
      isUnmounted.current = true;
      observer.disconnect();
    };
  }, [visibilityThreshold]);

  const contextValue: OverflowCounterState = useMemo(() => {
    const val = {
      observeElement: (el: HTMLElement) => {
        observerRef.current?.observe(el);
        elementsToObserve.current.push(el);
        return () => val.unobserveElement(el);
      },
      unobserveElement: (el: HTMLElement) => {
        // No need to cleanup anything if component is unmounted, because overflow counter is being destroyed
        if (!isUnmounted.current) {
          elementsToObserve.current = elementsToObserve.current.filter(
            (observableEl: HTMLElement) => observableEl !== el
          );
          setHiddenElements((keys) => {
            const overflowKey = el.getAttribute('data-overflowkey');
            if (overflowKey && keys.includes(overflowKey)) {
              return keys.filter((key) => key !== overflowKey);
            }
            return keys;
          });
        }
        observerRef.current?.unobserve(el);
      },
    };
    return val;
  }, []);

  return (
    <OverflowCounterContext.Provider value={contextValue}>
      <HiddenElementsContext.Provider value={hiddenElements.length}>
        <Box ref={ref} {...props} />
      </HiddenElementsContext.Provider>
    </OverflowCounterContext.Provider>
  );
};
